import { ComponentProps, ReactNode, forwardRef, useCallback, useEffect, useId, useMemo, useRef, type JSX } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import useAnimateInOutComponent from '@alltrails/core/hooks/useAnimateInOutComponent';
import useScreenLock from '@alltrails/core/hooks/useScreenLock';
import useScroll from '@alltrails/core/hooks/useScroll';
import Typography from '@alltrails/core/components/Typography';
import type { FocusTargetOrFalse } from 'focus-trap';
import { Size } from '@alltrails/denali/types';
import IconButton from '@alltrails/denali/components/IconButton';
import PagedProgressBar from '@alltrails/denali/components/PagedProgressBar';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import Close from '@alltrails/denali/icons/Close';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import FooterButtons from '../FooterButtons';
import styles from './styles/Modal.module.scss';

type ModalProps = {
  testId?: string;
  allowOutsideClicks?: boolean;
  ariaLabelElementId?: string;
  buttons?: Pick<ComponentProps<typeof FooterButtons>, 'fill' | 'defaultButton' | 'flatButton' | 'primaryButton'>;
  caption?: ReactNode;
  children: ReactNode;
  contentsClassName?: string;
  focusTrapOptions?: ComponentProps<typeof FocusTrap>['focusTrapOptions'];
  hidePageScrollbarGutter?: boolean;
  initialFocus?: FocusTargetOrFalse;
  isDeviceHeight?: boolean;
  isOpen: boolean;
  isPaused?: boolean;
  modalClassName?: string;
  backgroundClassName?: string;
  titleClassName?: string;
  onBackRequest?: () => void;
  onCloseComplete?: () => void;
  onCloseRequest: () => void;
  shouldHideHeader?: boolean;
  shouldHideCloseButton?: boolean;
  pageInfo?: Pick<ComponentProps<typeof PagedProgressBar>, 'index' | 'total'>;
  size?: Size<'sm' | 'md' | 'lg' | 'xl'>;
  dimensionsTheme?: 'default' | 'compact';
  ariaLabel?: string;
} & (
  | {
      headerImage?: never;
      subtitle?: ReactNode;
      title?: ReactNode;
    }
  | {
      headerImage?: ReactNode;
      subtitle?: never;
      title?: never;
    }
);

// https://www.figma.com/design/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=3084-40503
const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      testId,
      allowOutsideClicks = false,
      ariaLabelElementId,
      buttons,
      caption,
      children,
      contentsClassName,
      focusTrapOptions,
      headerImage,
      hidePageScrollbarGutter = false,
      initialFocus,
      isDeviceHeight = false,
      isOpen,
      isPaused = false,
      modalClassName,
      backgroundClassName,
      titleClassName,
      onBackRequest,
      onCloseRequest,
      onCloseComplete,
      pageInfo,
      size = 'md',
      subtitle,
      title,
      shouldHideHeader = false,
      shouldHideCloseButton = false,
      dimensionsTheme = 'default',
      ariaLabel
    },
    ref
  ): JSX.Element | null => {
    const { isAnimating, isVisible, onAnimationEnd } = useAnimateInOutComponent(isOpen, onCloseComplete);
    useScreenLock(isVisible, hidePageScrollbarGutter);
    const intl = useIntl();
    const isMobile = useIsMobileSizedScreen();
    const scrollContainer = useRef<HTMLDivElement>(null);
    const headerImageContainer = useRef<HTMLDivElement>(null);
    const { amountScrolled, canScrollDown, canScrollUp, updateScroll } = useScroll(scrollContainer, true, isVisible);

    // Keep track of open state locally to avoid multiple onCloseRequest calls when a user clicks the close button - once for the button, a second time for onDeactivate.
    // This is preferred over a debounce because it handles the case where onCloseRequest changes the onCloseRequest definition, which can also lead to multiple calls.
    const isClosed = useRef(false);
    useEffect(() => {
      isClosed.current = !isOpen;
    }, [isOpen]);
    const closeModal = useCallback(() => {
      if (!isClosed.current) {
        onCloseRequest();
      }
      isClosed.current = true;
    }, [onCloseRequest]);

    const isImageModal = useMemo(() => Boolean(headerImage), [headerImage]);
    const internalTitleId = useId();

    const titleElement = useMemo(() => {
      if (title) {
        return (
          <div className={classNames(styles.titleContainer, titleClassName)}>
            <Typography id={internalTitleId} component="h2" className={styles.title} variant={isMobile ? 'heading300' : 'heading400'}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="text200" color="secondary">
                {subtitle}
              </Typography>
            )}
          </div>
        );
      }
      return null;
    }, [isMobile, subtitle, title, titleClassName, internalTitleId]);

    const showFooter = Boolean(buttons || pageInfo);

    // When the image has been scrolled half out of view, we want to start making the header opaque rather than transparent.
    // We do this gradually until the entire image has been scrolled out of view, at which point the header will be completely opaque.
    const headerStyle = useMemo(() => {
      if (isImageModal && headerImageContainer.current) {
        const imageHeight = headerImageContainer.current.clientHeight;
        const halfImageHeight = imageHeight / 2;

        let opacity: number;
        if (amountScrolled >= imageHeight) {
          opacity = 100;
        } else if (amountScrolled <= halfImageHeight) {
          opacity = 0;
        } else {
          opacity = 100 * ((amountScrolled - halfImageHeight) / halfImageHeight);
        }

        return { backgroundColor: `color-mix(in srgb, currentColor ${opacity}%, transparent)` };
      }
      return undefined;
    }, [amountScrolled, isImageModal]);

    if (!isAnimating && !isVisible) {
      return null;
    }

    return (
      <ModalPortal>
        <div
          ref={ref}
          data-testid={testId}
          // The modal portal is rendered outside of the div, so it is necessary to add theming here
          data-at-theme-dimensions={dimensionsTheme}
          className={classNames(styles.background, backgroundClassName, {
            [styles.isAnimating]: isAnimating,
            [styles.isVisible]: isVisible,
            [styles.transparent]: allowOutsideClicks
          })}
          onAnimationEnd={onAnimationEnd}
          id="modalBackground" // Needed by legacy hooks and components
        >
          <FocusTrap
            active={isVisible}
            paused={isPaused}
            focusTrapOptions={{
              clickOutsideDeactivates: !allowOutsideClicks,
              escapeDeactivates: true,
              onDeactivate: closeModal,
              initialFocus,
              ...focusTrapOptions
            }}
          >
            {/* Need to stop propagation of any animationEnd event within the modal so that it doesn't trigger the onAnimationEnd of the modalBackground component */}
            <div
              className={classNames(styles.modal, styles[size], modalClassName, {
                [styles.isDeviceHeight]: isDeviceHeight
              })}
              role="dialog"
              aria-modal="true"
              {...(ariaLabel
                ? { 'aria-label': ariaLabel }
                : typeof title === 'string'
                  ? { 'aria-label': title }
                  : ariaLabelElementId
                    ? { 'aria-labelledby': ariaLabelElementId }
                    : {})}
              onAnimationEnd={e => e.stopPropagation()}
            >
              {!shouldHideHeader && (
                <div
                  className={classNames(styles.header, { [styles.imageModal]: isImageModal, [styles.bottomBorder]: canScrollUp })}
                  style={headerStyle}
                >
                  {onBackRequest ? (
                    <IconButton
                      icon={{ Component: ArrowSq, orientation: 'left' }}
                      onClick={onBackRequest}
                      size="sm"
                      testId="modal-back"
                      title={intl.formatMessage({ defaultMessage: 'Go back' })}
                      variant={isImageModal ? 'elevated' : 'default'}
                    />
                  ) : (
                    <div className={styles.buttonPlaceholder} />
                  )}
                  {isMobile && titleElement}
                  {!shouldHideCloseButton && (
                    <IconButton
                      icon={{ Component: Close }}
                      onClick={closeModal}
                      size="sm"
                      testId="modal-close"
                      title={intl.formatMessage({ defaultMessage: 'Close' })}
                      variant={isImageModal ? 'elevated' : 'default'}
                      className={styles.closeButton}
                    />
                  )}
                </div>
              )}
              <div className={styles.scrollContainer} onScroll={updateScroll} ref={scrollContainer} id="modal-scroll-container">
                {isImageModal && (
                  <div className={styles.headerImageContainer} ref={headerImageContainer}>
                    {headerImage}
                  </div>
                )}
                <div className={classNames(styles.contents, contentsClassName)}>
                  {!isMobile && titleElement}
                  {children}
                </div>
              </div>
              {showFooter && (
                <div className={classNames(styles.footer, { [styles.topBorder]: canScrollDown })}>
                  {pageInfo && <PagedProgressBar className={styles.pagedProgressBar} {...pageInfo} />}
                  {buttons && <FooterButtons {...buttons} fill={buttons.fill ?? isMobile} />}
                  {caption && <div className={styles.caption}>{caption}</div>}
                </div>
              )}
            </div>
          </FocusTrap>
        </div>
      </ModalPortal>
    );
  }
);

export default Modal;
